import * as React from "react";
import { formInitialize, formUninitialize, useCpFormSubmit } from "../../gvc/testNewForm/utils";
import { v4 as uuidv4 } from "uuid";
import { homeLink, linksOf, request } from "../../../services/cpln";
import { useBlocker } from "react-router-dom";
import { sentryLog } from "../../../tools/sentry";
import { notification } from "antd";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpFormPrompt } from "../../../components/antd/form/CpFormPrompt";
import { getValidator } from "./validator";
import { CpPreviewModal } from "../../workload/draft/previewModal";
import { CpDebugModal } from "../../workload/draft/debugModal";
import { getDefaultAgentInput, inputToAgent } from "../utils";
import { AgentInput } from "../../../schema/types/agent/input";
import { AgentCreateContent } from "./content";
import { Agent } from "../../../schema/types/agent/entity";
import { BootstrapConfigModal } from "./bootstrapConfigModal";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { agentApi } from "../../../store/api/agentApi";

interface Props {}
export const AgentCreateForm: React.FC<Props> = () => {
  const dispatch = useAppDispatch();

  const [createdAgent, setCreatedAgent] = React.useState<Agent>(null as any);

  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = getDefaultAgentInput();
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // prompt
  const ignoreBlockerRef = React.useRef(false);
  const nextLocationRef = React.useRef("/");
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    nextLocationRef.current = nextLocation.pathname;

    const res =
      !ignoreBlockerRef.current &&
      currentLocation.pathname.includes(`/agent/-create`) &&
      !nextLocation.pathname.includes(`/agent/-create`);
    if (res) {
      sentryLog("blockedNavigation", {
        "data.ignoreBlockerRef": ignoreBlockerRef.current,
        "pathname.next": nextLocation.pathname,
        "pathname.current": currentLocation.pathname,
        "pathname.check": "-create",
        res: String(res),
      });
    }

    return res;
  });

  // validation/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  function onDone(agentLink: string) {
    notification.success({
      message: "Success",
      description: "Agent is created",
    });
    dispatch(setRedirect(`/console${agentLink}`));
  }

  async function handleSubmit(input: AgentInput) {
    try {
      let body = inputToAgent(input);
      const method = "post";
      const url = homeLink("agent");

      const { data: createdAgent } = await request({
        method: method,
        url: url,
        body: body,
      });
      const agentSelfLink = linksOf(createdAgent).self!;
      dispatch(agentApi.util.invalidateTags([{ type: "Agent", id: agentSelfLink }]));
      setCreatedAgent(createdAgent);
      ignoreBlockerRef.current = true;
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <>
      <CpFormContext.Provider value={{ id: formId, submit: submit }}>
        <AgentCreateContent ignoreBlockerRef={ignoreBlockerRef} />
        <CpFormValidator validationFn={validator} />
        <CpFormPrompt blocker={blocker} nextLocationRef={nextLocationRef} />
        <CpPreviewModal inputToItemFn={inputToAgent} kind="agent" namePath="name" title="Agent" />
        <CpDebugModal inputToItemFn={inputToAgent} kind="agent" namePath="name" title="Agent" />
      </CpFormContext.Provider>
      {createdAgent === null ? null : (
        <BootstrapConfigModal agent={createdAgent} onDone={onDone} visible={createdAgent !== null} />
      )}
    </>
  );
};
